import { Component, OnInit, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { SummaryReportService } from '../../services/summary-report.service';
import {
    SummaryReportDto,
    MixReportDto,
    RheologyReportDto,
    RheologyTypes,
    BobAndSleeveTypes,
    CompStrengthTypes,
    ThickeningTimeReportDto,
    FluidLossTypes,
    HpHtRheologyTypes,
    StaticGelStrengthTypes
} from '../../services/summary-report.service';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';
import { PrintService } from '../../shared/print/print-dialog.component';
export interface SummaryReportDialogData {
    projectId: number;
}
@Component({
    selector: 'app-summary-report',
    standalone: true,
    imports: [CommonModule, SpinnerComponent, MatDialogModule, MatButtonModule, MatIconModule],
    templateUrl: './summary-report.component.html',
    styleUrls: ['./summary-report.component.css']
})
export class SummaryReportComponent implements OnInit {
    summaryReport: SummaryReportDto | null = null;
    isLoading = false;
    errorMessage = '';
    // Enums for template
    RheologyTypes = RheologyTypes;
    BobAndSleeveTypes = BobAndSleeveTypes;
    CompStrengthTypes = CompStrengthTypes;
    HpHtRheologyTypes = HpHtRheologyTypes;
    StaticGelStrengthTypes = StaticGelStrengthTypes;
    constructor(
        private summaryReportService: SummaryReportService,
        private printService: PrintService,
        public dialogRef: MatDialogRef<SummaryReportComponent>,
        @Inject(MAT_DIALOG_DATA) public data: SummaryReportDialogData
    ) { }
    ngOnInit(): void {
        if (this.data.projectId) {
            this.loadSummaryReport();
        }
    }
    loadSummaryReport(): void {
        if (!this.data.projectId) return;
        this.isLoading = true;
        this.errorMessage = '';
        this.summaryReport = null;
        this.summaryReportService.getSummaryReport(this.data.projectId).subscribe({
            next: (data) => {
                this.summaryReport = data;
                console.log('Summary Report Data:', this.summaryReport);
                // Log each mix's compStrengthReport
                this.summaryReport?.mixes?.forEach((mix, index) => {
                    console.log(`Mix ${index} (${mix.name}) compStrengthReport:`, mix.compStrengthReport);
                });
                this.isLoading = false;
            },
            error: (error) => {
                console.error('Error loading summary report:', error);
                this.errorMessage = 'Failed to load summary report. Please try again.';
                this.isLoading = false;
            }
        });
    }
    /**
     * Get rheology type display name
     */
    getRheologyTypeName(type: RheologyTypes): string {
        switch (type) {
            case RheologyTypes.Surface:
                return 'Surface';
            case RheologyTypes.Downhole:
                return 'Downhole';
            default:
                return 'Unknown';
        }
    }
    /**
     * Get bob and sleeve type display name
     */
    getBobAndSleeveTypeName(type: BobAndSleeveTypes): string {
        switch (type) {
            case BobAndSleeveTypes.R1B1:
                return 'R1B1';
            case BobAndSleeveTypes.R1B5:
                return 'R1B5';
            case BobAndSleeveTypes.Fysa:
                return 'FYSA';
            default:
                return 'Unknown';
        }
    }
    /**
     * Get compressive strength type display name
     */
    getCompStrengthTypeName(type: CompStrengthTypes): string {
        switch (type) {
            case CompStrengthTypes.Uca:
                return 'Uca';
            case CompStrengthTypes.Crush:
                return 'Crush';
            case CompStrengthTypes.ConditionedUCA:
                return 'Conditioned UCA';
            case CompStrengthTypes.FoamUca:
                return 'Foam Uca';
            case CompStrengthTypes.FoamCrush:
                return 'Foam Crush';
            case CompStrengthTypes.FoamConditionedUCA:
                return 'Foam Conditioned UCA';
            default:
                return 'Unknown';
        }
    }
    /**
     * Get surface rheology reports for a mix
     */
    getSurfaceRheology(mix: MixReportDto): RheologyReportDto[] {
        return mix.rheologyReports?.filter(r => r.type === RheologyTypes.Surface) || [];
    }
    /**
     * Get downhole rheology reports for a mix
     */
    getDownholeRheology(mix: MixReportDto): RheologyReportDto[] {
        return mix.rheologyReports?.filter(r => r.type === RheologyTypes.Downhole) || [];
    }
    /**
     * Get fluid loss type display name
     */
    getFluidLossTypeName(type: FluidLossTypes | number | undefined): string {
        switch (type) {
            case FluidLossTypes.Static:
                return 'Static';
            case FluidLossTypes.Stirred:
                return 'Stirred';
            default:
                return 'Unknown';
        }
    }
    getSortedFluidLossReports(reports: any[]) {
        return [...reports].sort((a, b) => a.type - b.type);
    }
    getHphtTypeName(type: HpHtRheologyTypes | number | undefined): string {
        switch (type) {
            case HpHtRheologyTypes.Fan77:
                return 'Fann 77';
            case HpHtRheologyTypes.Chann7600:
                return 'Chann 7600';
            case HpHtRheologyTypes.Chann7550:
                return 'Chann 7550';
            default:
                return 'Unknown';
        }
    }
    getStaticGelTypeName(type: StaticGelStrengthTypes | number | undefined): string {
        switch (type) {
            case StaticGelStrengthTypes.SGSA:
                return 'SGSA';
            case StaticGelStrengthTypes.MACS:
                return 'MACS';
            default:
                return 'Unknown';
        }
    }
    /**
     * Get schedule text from thickening time report - matches C# converter logic
     */
    getScheduleText(report: ThickeningTimeReportDto | null | undefined): string {
        if (!report) return '';
        const initTemp = report.initialTemperature || 0;
        const initPressure = report.initialPressure || 0;
        const finalTemp = report.finalTemperature || 0;
        const finalPressure = report.finalPressure || 0;
        const tempRampTime = report.temperatureRampTime || 0;
        const pressureRampTime = report.pressureRampTime || 0;
        const stopMotor = report.stopMotor || 0;
        const startMotor = report.startMotor || 0;
        const conditioningTime = report.conditioningTime || 0;
        const gradient = report.gradient || 0;
        const bhst = report.bhst || 0;
        const isHesitation = report.isHesitation || false;
        const isShutdown = report.isShutdown || false;
        let schedule = `Start at ${initTemp}°F and ${initPressure} psi. `;
        schedule += `Ramp temperature to ${finalTemp}°F in ${tempRampTime} min. `;
        schedule += `Ramp pressure to ${finalPressure} psi in ${pressureRampTime} min. `;
        if (isHesitation && isShutdown) {
            // HesitationShutdownSchedule
            schedule += `Hold pressure for duration. `;
            schedule += `Shutdown motor after ${stopMotor} min for ${startMotor - stopMotor} min then start motor back up at ${startMotor} min. `;
            schedule += `Hold temperature for ${conditioningTime} min then begin hesitation cycles while increasing temperature at ${gradient.toFixed(2)}°F/min until reaching ${bhst}°F.`;
        } else if (isHesitation) {
            // HesitationSchedule
            schedule += `Hold pressure for duration. `;
            schedule += `Hold temperature for ${conditioningTime} min then begin hesitation cycles while increasing temperature at ${gradient.toFixed(2)}°F/min until reaching ${bhst}°F.`;
        } else if (isShutdown) {
            // ShutdownSchedule
            schedule += `Hold temperature and pressure for duration. `;
            schedule += `Shutdown motor after ${stopMotor} min for ${startMotor - stopMotor} min then start motor back up at ${startMotor} min. Then run for duration.`;
        } else {
            // GeneralSchedule
            schedule += `Hold temperature and pressure for duration of the test.`;
        }
        return schedule;
    }
    /**
     * Print the summary report.     
     */
    printReport(): void {
        const contentEl = document.querySelector<HTMLElement>('.summary-content');
        this.printService.print(contentEl, {
            pageSize: 'A4 portrait',
            colorMap: {
                'bg-yellow': { bg: '#FAAB18' },
                'bg-green': { bg: '#b2cc34' },
                'bg-cyan': { bg: '#00b2bd' },
                'bg-orange': { bg: '#E5601F'},
            },
            decorators: [
                {
                    selector: 'tr.bg-red > th, tr.bg-red > td',
                    style: 'background-color: #e21836 !important; color: #000 !important; text-align: center !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;',
                },
                {
                    selector: 'tr.bg-gray > th, tr.bg-gray > td',
                    style: 'background-color: #ba3093 !important; color: #fff !important; text-align: center !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;',
                },
                {
                    selector: 'tr.bg-purple > th, tr.bg-purple > td',
                    style: 'background-color: #751269 !important; color: #fff !important; text-align: center !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;',
                },
                {
                    selector: 'tr.bg-blue > th, tr.bg-blue > td',
                    style: 'background-color: #009DD9 !important; color: #fff !important; text-align: center !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;',
                },
                {
                    selector: '.custom-mix-table, .mix-header-table',
                    style: 'background-color: #fff !important; border: 1px solid #000 !important; border-collapse: collapse !important; page-break-inside: avoid !important; break-inside: avoid !important; width: 100% !important; table-layout: auto !important; font-size: 11px !important;',
                },
                {
                    selector: '.project-info-header tr, .project-info-header td, .project-info-header div',
                    style: 'background-color: #fff !important;',
                },
                {
                    selector: '.custom-mix-table:not(.project-info-header) tr, .custom-mix-table:not(.project-info-header) td, .mix-header-table:not(.project-info-header) tr, .mix-header-table:not(.project-info-header) td',
                    style: 'background-color: #FAAB18 !important; border: none !important; padding: 2px 6px !important; vertical-align: middle; line-height: 1.3; word-wrap: break-word !important; overflow-wrap: break-word !important; white-space: normal !important;',
                },
                {
                    selector: 'tr.bg-orange > th, tr.bg-orange > td',
                    style: 'background-color: #E5601F !important; border: 1px solid #000 !important; padding: 2px 6px !important; vertical-align: middle; line-height: 1.3;',
                },
                {
                    selector: 'table.table-bordered, table.table-bordered th, table.table-bordered td',
                    style: `border: 1px solid #000 !important; border-collapse: collapse !important;`,
                },
                {
                    selector: '.mixability-row td',
                    style: 'border-bottom: 1px solid #000 !important;',
                },
                {
                    selector: '.test-table, .rheology-table',
                    style: 'outline: 1px solid #000 !important; outline-offset: -1px !important; border-collapse: collapse !important; page-break-inside: avoid !important; break-inside: avoid !important; width: 100% !important; table-layout: auto !important;',
                },
                {
                    selector: '.test-table th, .test-table td, .rheology-table th, .rheology-table td',
                    style: 'border: 1px solid #000 !important; word-wrap: break-word !important; overflow-wrap: break-word !important;',
                },
                {
                    selector: '.notes-section',
                    style: 'border: 1px solid #000 !important; page-break-inside: avoid !important; break-inside: avoid !important; word-wrap: break-word !important; overflow-wrap: break-word !important;',
                },
                { selector: '.white-text', style: 'color: #fff !important;' },
                { selector: '.center', style: 'text-align: center !important;' },
                { 
                    selector: '.inline-item', 
                    style: 'display: inline; margin-right: 12px; white-space: normal !important; word-wrap: break-word !important; overflow-wrap: break-word !important;' 
                },
                {
                    selector: '.custom-mix-table td, .mix-header-table td, .test-table td, .test-table th, .rheology-table td, .rheology-table th',
                    style: 'color: #000 !important; font-weight: bold !important; word-wrap: break-word !important; overflow-wrap: break-word !important; max-width: 100% !important;',
                },
                {
                    selector: '.summary-content, .container-fluid',
                    style: 'max-width: 100% !important; overflow: hidden !important; box-sizing: border-box !important;',
                },
                {
                    selector: '.mix-section',
                    style: 'max-width: 100% !important; overflow: hidden !important;',
                },
                {
                    selector: 'body',
                    style: 'margin: 0.5in !important; padding: 0 !important;',
                },
            ],
            fallbackBodyClass: 'summary-report-printing',
        });
    }
    /**
     * Close the dialog
     */
    close(): void {
        this.dialogRef.close();
    }
    // ── Window controls: minimize / maximize ──────────────────────────
    isMaximized = false;
    isMinimized = false;
    toggleMaximize(): void {
        // Coming out of minimized state if needed
        if (this.isMinimized) {
            this.dialogRef.removePanelClass('summary-report-minimized');
            this.isMinimized = false;
        }
        if (!this.isMaximized) {
            this.dialogRef.addPanelClass('summary-report-maximized');
            this.isMaximized = true;
        } else {
            this.dialogRef.removePanelClass('summary-report-maximized');
            this.isMaximized = false;
        }
    }
    toggleMinimize(): void {
        // If currently maximized, drop that first
        if (this.isMaximized) {
            this.dialogRef.removePanelClass('summary-report-maximized');
            this.isMaximized = false;
        }
        this.isMinimized = !this.isMinimized;
        if (this.isMinimized) {
            this.dialogRef.addPanelClass('summary-report-minimized');
        } else {
            this.dialogRef.removePanelClass('summary-report-minimized');
        }
    }
    formatTime(value: number | null | undefined): string {
        if (value === null || value === undefined) {
            return '';
        }
        const totalMinutes = Math.round(value);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${hours}:${minutes.toString().padStart(2, '0')}`;
    }
    /**
     * Get confined CS type display name
     */
    getConfCSTypeName(type: number): string {
        switch (type) {
            case 0:
                return 'Tangent';
            case 1:
                return 'Average';
            case 2:
                return 'Secant';
            default:
                return '';
        }
    }
}
